import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { Company } from "../lib/access";
import { Modal } from "../components/Modal";
import { Routing } from "./Routing";

export interface PhoneNumber {
  id: string;
  e164: string;
  state: "provisioning" | "live" | "suspended" | "released";
  department_id: string | null;
  configured_at: string | null;
  config_error: string | null;
}
interface Request {
  id: string; country: string; quantity: number; purpose: string | null;
  status: string; created_at: string;
}
interface Dept { id: string; name: string }

const STATE_WORDS: Record<PhoneNumber["state"], string> = {
  provisioning: "Being set up",
  live: "Live",
  suspended: "Suspended",
  released: "Released",
};

export function Numbers({ company }: { company: Company }) {
  const [numbers, setNumbers] = useState<PhoneNumber[] | null>(null);
  const [requests, setRequests] = useState<Request[]>([]);
  const [depts, setDepts] = useState<Dept[]>([]);
  const [editing, setEditing] = useState<PhoneNumber | null>(null);
  const [purpose, setPurpose] = useState("");
  const [deptId, setDeptId] = useState<string>("");
  const [msg, setMsg] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [n, r, d] = await Promise.all([
      supabase.from("phone_numbers")
        .select("id,e164,state,department_id,configured_at,config_error")
        .eq("company_id", company.id).order("created_at"),
      supabase.from("number_requests")
        .select("id,country,quantity,purpose,status,created_at")
        .eq("company_id", company.id).neq("status", "allocated")
        .order("created_at", { ascending: false }),
      supabase.from("departments").select("id,name").eq("company_id", company.id),
    ]);
    setNumbers((n.data as PhoneNumber[]) ?? []);
    setRequests((r.data as Request[]) ?? []);
    setDepts((d.data as Dept[]) ?? []);
  }, [company.id]);

  useEffect(() => { void load(); }, [load]);

  async function request(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    const { data: sess } = await supabase.auth.getSession();
    const { data: row, error } = await supabase.from("number_requests").insert({
      company_id: company.id,
      department_id: deptId || null,
      purpose,
      requested_by: sess.session?.user.id,
    }).select("id").single();

    if (error) {
      setBusy(false);
      return setMsg("You do not have permission to request a number.");
    }

    // Tell us a client is waiting. The request is already saved, so a failure
    // to send the email must not read as a failure to request the number.
    await fetch(`${import.meta.env.VITE_API_URL}/notifications/number-requested`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sess.session?.access_token ?? ""}`,
      },
      body: JSON.stringify({ request_id: row.id }),
    }).catch(() => undefined);

    setBusy(false);
    setPurpose("");
    setDeptId("");
    setAsking(false);
    setMsg("Requested. We will allocate a number and it will appear here.");
    void load();
  }

  async function assignDept(id: string, department_id: string) {
    await supabase.from("phone_numbers")
      .update({ department_id: department_id || null }).eq("id", id);
    void load();
  }

  if (editing) {
    return (
      <Routing company={company} number={editing} onBack={() => { setEditing(null); void load(); }} />
    );
  }

  return (
    <>
      <header className="page-head row">
        <div>
          <h1>Phone numbers</h1>
          <p className="muted">
            Ask for a number and we allocate it. A number belongs to a department, and that
            is what decides whose calls these are.
          </p>
        </div>
        <button className="btn" onClick={() => { setMsg(null); setAsking(true); }}>
          Request a number
        </button>
      </header>

      {msg && !asking && <p className="status">{msg}</p>}

      {numbers === null ? <p className="muted">Loading…</p>
        : numbers.length === 0 && requests.length === 0
        ? <div className="card"><p className="muted">No numbers yet.</p></div>
        : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Number</th><th>Department</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {numbers.map((n) => (
                  <tr key={n.id}>
                    <td><b>{n.e164}</b>
                      {n.config_error && <span className="sub err">{n.config_error}</span>}</td>
                    <td>
                      <select value={n.department_id ?? ""}
                        onChange={(e) => void assignDept(n.id, e.target.value)}>
                        <option value="">Unassigned</option>
                        {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </td>
                    <td>
                      <span className={"state " + n.state}>{STATE_WORDS[n.state]}</span>
                    </td>
                    <td className="right">
                      <button className="btn ghost small" onClick={() => setEditing(n)}>
                        Configure
                      </button>
                    </td>
                  </tr>
                ))}
                {requests.map((r) => (
                  <tr key={r.id} className="pending">
                    <td><b>Requested</b><span className="sub">{r.purpose ?? "—"}</span></td>
                    <td className="muted">—</td>
                    <td><span className="state provisioning">With Hauzisha</span></td>
                    <td></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {asking && (
        <Modal
          title="Request a number"
          description="Tell us what it is for and which team answers it. We buy it and set it up."
          onClose={() => setAsking(false)}
        >
          <form onSubmit={request}>
            <label htmlFor="pp">What is it for</label>
            <input id="pp" required value={purpose} placeholder="Main sales line"
              onChange={(e) => setPurpose(e.target.value)} />

            <label htmlFor="dp">Department</label>
            <select id="dp" value={deptId} onChange={(e) => setDeptId(e.target.value)}>
              <option value="">Decide later</option>
              {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>

            {msg && <p className="status error">{msg}</p>}

            <div className="actions">
              <button className="btn" type="submit" disabled={busy || !purpose.trim()}>
                {busy ? "Sending…" : "Request a number"}
              </button>
              <button className="btn ghost" type="button" onClick={() => setAsking(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
